/**
 * @Description: 自定义 Composable 用于文本转语音功能（使用系统本地音色，默认音色随环境语言切换）
 * @file useSpeech.ts
 * @author hu_jie
 * @date 2025/3/14
 */

import { ref, computed, watch } from 'vue';
import { useLocaleStoreWithOut } from '/@/store/modules/locale';
import { holdRefresh, releaseRefresh } from '/@/utils/autoRefresh/refreshGuard';

// 存储配置的 key
const VOICE_STORAGE_KEY = 'speech_voice_uri';
const RATE_STORAGE_KEY = 'speech_rate';
const PITCH_STORAGE_KEY = 'speech_pitch';

// 默认配置
const DEFAULT_RATE = 0.9;
const DEFAULT_PITCH = 1.1;

// 全局单例状态（在模块级别定义，确保跨组件共享）
const voices = ref<SpeechSynthesisVoice[]>([]);
const selectedVoice = ref<SpeechSynthesisVoice | null>(null);
const isVoicesLoaded = ref(false);
const isPlaying = ref(false);
const rate = ref(DEFAULT_RATE);
const pitch = ref(DEFAULT_PITCH);

// 播放控制变量（模块级别）
let shouldContinue = false;
let currentPlayCount = 0;
let targetCount = 1;
let currentText = '';
let isInitialized = false;

// 当前环境的语言判断（中/英），用于默认音色与朗读语言随环境切换
const localeStore = useLocaleStoreWithOut();
const isEn = computed(() => localeStore.getLocale === 'en');
const currentLangCode = computed(() => (isEn.value ? 'en-US' : 'zh-CN'));

/**
 * 过滤出本地音色（不区分语言，始终展示系统已安装的本地音色）
 */
const filterLocalVoices = (voiceList: SpeechSynthesisVoice[]) => {
  return voiceList.filter((voice) => voice.localService === true);
};

/**
 * 获取音色的唯一标识
 */
const getVoiceKey = (voice: SpeechSynthesisVoice): string => {
  return voice.voiceURI;
};

/**
 * 加载所有可用的音色
 */
const loadVoices = () => {
  const availableVoices = window.speechSynthesis.getVoices();
  if (availableVoices.length > 0) {
    const localVoices = filterLocalVoices(availableVoices);
    voices.value = localVoices;
    isVoicesLoaded.value = true;

    if (localVoices.length === 0) {
      console.warn('没有找到本地音色，请确保系统已安装语音包');
      return;
    }

    // 恢复之前保存的语速和音调
    const savedRate = localStorage.getItem(RATE_STORAGE_KEY);
    if (savedRate !== null) {
      rate.value = parseFloat(savedRate);
    }
    const savedPitch = localStorage.getItem(PITCH_STORAGE_KEY);
    if (savedPitch !== null) {
      pitch.value = parseFloat(savedPitch);
    }

    // 恢复之前保存的音色
    const savedVoiceURI = localStorage.getItem(VOICE_STORAGE_KEY);
    if (savedVoiceURI) {
      const savedVoice = localVoices.find((voice) => voice.voiceURI === savedVoiceURI);
      if (savedVoice) {
        selectedVoice.value = savedVoice;
        return;
      }
    }

    // 如果没有保存的音色，设置默认音色（优先当前环境语言的音色）
    const defaultVoice =
      localVoices.find((voice) => voice.lang === currentLangCode.value) || localVoices[0];
    if (defaultVoice) {
      selectedVoice.value = defaultVoice;
      localStorage.setItem(VOICE_STORAGE_KEY, getVoiceKey(defaultVoice));
    }
  }
};

/**
 * 内部递归播放函数
 */
const speakNext = () => {
  if (
    (currentPlayCount >= targetCount && targetCount !== -1) ||
    (!shouldContinue && targetCount === -1)
  ) {
    isPlaying.value = false;
    return;
  }

  const utterance = new window.SpeechSynthesisUtterance(currentText);

  const activeVoice = selectedVoice.value ?? voices.value[0];
  if (activeVoice) {
    utterance.voice = activeVoice;
    // 朗读语言跟随所选音色；未选中音色时按当前环境语言
    utterance.lang = activeVoice.lang;
  } else {
    utterance.lang = currentLangCode.value;
  }

  utterance.rate = rate.value;
  utterance.pitch = pitch.value;

  utterance.addEventListener(
    'end',
    () => {
      currentPlayCount++;
      if (
        (targetCount === -1 || currentPlayCount < targetCount) &&
        (targetCount !== -1 || shouldContinue)
      ) {
        speakNext();
      } else {
        isPlaying.value = false;
      }
    },
    { once: true },
  );

  utterance.addEventListener(
    'error',
    (event) => {
      console.error('语音播放错误:', event);
      isPlaying.value = false;
      shouldContinue = false;
    },
    { once: true },
  );

  window.speechSynthesis.speak(utterance);
};

/**
 * 播放文本转语音
 */
const playTextToSpeech = (text: string, count = 1) => {
  if (!('speechSynthesis' in window)) {
    console.error('浏览器不支持语音合成');
    return;
  }

  if (voices.value.length === 0) {
    console.warn('没有可用的本地音色');
    return;
  }

  stopTextToSpeech();

  currentPlayCount = 0;
  targetCount = count;
  currentText = text;
  shouldContinue = count === -1;

  speakNext();
  isPlaying.value = true;
};

/**
 * 主动停止当前语音播放
 */
const stopTextToSpeech = () => {
  shouldContinue = false;
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
  currentPlayCount = 0;
  isPlaying.value = false;
};

/**
 * 设置音色
 */
const setVoice = (voice: SpeechSynthesisVoice) => {
  if (!voice.localService) {
    console.warn('只能选择本地音色');
    return;
  }
  selectedVoice.value = voice;
  localStorage.setItem(VOICE_STORAGE_KEY, getVoiceKey(voice));
};

/**
 * 设置语速
 */
const setRate = (value: number) => {
  const newRate = Math.min(2, Math.max(0.5, value));
  rate.value = newRate;
  localStorage.setItem(RATE_STORAGE_KEY, newRate.toString());
};

/**
 * 设置音调
 */
const setPitch = (value: number) => {
  const newPitch = Math.min(1.5, Math.max(0.5, value));
  pitch.value = newPitch;
  localStorage.setItem(PITCH_STORAGE_KEY, newPitch.toString());
};

/**
 * 获取按语言分组后的音色列表
 */
const getVoicesByLang = computed(() => {
  const grouped: Record<string, SpeechSynthesisVoice[]> = {};
  voices.value.forEach((voice) => {
    const langKey = voice.lang;
    if (!grouped[langKey]) {
      grouped[langKey] = [];
    }
    grouped[langKey].push(voice);
  });
  return grouped;
});

/**
 * 检查是否有可用音色
 */
const hasVoices = computed(() => voices.value.length > 0);

/**
 * 初始化语音系统（在 main.ts 中调用）
 */
const initSpeech = () => {
  if (isInitialized) {
    console.log('语音系统已初始化过');
    return;
  }

  if (!('speechSynthesis' in window)) {
    console.error('SpeechSynthesis API is not supported in this browser.');
    return;
  }

  console.log('开始初始化语音系统...');

  // 立即尝试获取已加载的语音
  const initialVoices = window.speechSynthesis.getVoices();
  if (initialVoices.length > 0) {
    loadVoices();
  }

  // 监听语音列表变化
  window.speechSynthesis.onvoiceschanged = () => {
    console.log('语音列表更新事件触发');
    loadVoices();
  };

  isInitialized = true;
  console.log('语音系统初始化完成');
};

/**
 * 等待语音列表加载完成（用于组件中需要等待的场景）
 */
const waitForVoices = (): Promise<void> => {
  return new Promise((resolve) => {
    if (isVoicesLoaded.value && voices.value.length > 0) {
      resolve();
      return;
    }

    if (isVoicesLoaded.value && voices.value.length === 0) {
      console.warn('语音列表已加载，但没有找到本地音色');
      resolve();
      return;
    }

    const stopWatch = watch([isVoicesLoaded, () => voices.value.length], ([loaded, len]) => {
      if (loaded) {
        stopWatch();
        if (len === 0) {
          console.warn('语音列表加载完成，但没有找到本地音色');
        }
        resolve();
      }
    });
  });
};

/**
 * 播放文本转语音（自动等待加载完成）
 */
const playTextToSpeechAuto = async (text: string, count = 1) => {
  await waitForVoices();
  playTextToSpeech(text, count);
};

// 监听音色变化，同步到 localStorage
watch(selectedVoice, (newVoice, oldVoice) => {
  if (newVoice && newVoice !== oldVoice) {
    localStorage.setItem(VOICE_STORAGE_KEY, getVoiceKey(newVoice));
  }
});

// 环境语言切换时，停止当前播放并重新加载音色（默认音色随环境语言切换）
watch(isEn, () => {
  if (isInitialized) {
    stopTextToSpeech();
    loadVoices();
  }
});

/**
 * 播报期间阻塞"部署后无感刷新"（HARD）。
 *
 * 两个理由：
 * 1. 播报中刷新会打断护士正在听的告警播报；
 * 2. speechSynthesis 是浏览器级服务，reload 不会取消正在朗读的 utterance，
 *    旧页面的语音会念完并与新页面的播报叠播。
 * 注意 playTextToSpeech(text, -1) 是无限循环（shouldContinue），
 * 那种情况靠 hardBlockMaxMs 兜底强制刷新，否则版本永远不会更新。
 */
watch(isPlaying, (playing) => {
  if (playing) {
    holdRefresh('speech', 'hard');
  } else {
    releaseRefresh('speech');
  }
});

/**
 * 页面卸载前取消朗读。
 *
 * 这是对一个独立既存 bug 的修复：用户手动按 F5 / 关标签页时，正在朗读的 utterance
 * 同样不会被取消，旧语音会与新页面加载后的播报叠播。
 * 属尽力而为——cancel() 在 pagehide 阶段是否被浏览器兑现由实现决定，但成本为零。
 */
window.addEventListener('pagehide', stopTextToSpeech);

/**
 * useSpeech Hook - 返回响应式数据和方法
 */
export function useSpeech() {
  return {
    // 响应式数据
    voices,
    selectedVoice,
    isVoicesLoaded,
    isPlaying,
    rate,
    pitch,
    hasVoices,

    // 便捷属性
    getVoicesByLang,

    // 方法
    playTextToSpeech,
    playTextToSpeechAuto,
    stopTextToSpeech,
    setVoice,
    setRate,
    setPitch,
  };
}

// 导出初始化方法（在 main.ts 中调用）
// stopTextToSpeech 用具名导出而非只挂在 useSpeech() 返回值上：
// 无需组件上下文的地方（如 src/utils/autoRefresh/prepareReload.ts 的刷新前清理）要直接调用它。
export { initSpeech, stopTextToSpeech };
